import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight, MessageCircle } from "lucide-react";
import { FadeUp, PageShell, Eyebrow } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { usePageTitle } from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PROJECT_TYPES = ["New Construction", "Renovation", "Farmhouse / Villa", "Waterproofing / Repairs", "Interior Work", "Other"];
const BUDGETS = ["Under ₹25L", "₹25L – ₹75L", "₹75L – ₹2Cr", "Above ₹2Cr", "Prefer to discuss"];

const initialForm = { name: "", email: "", phone: "", location: "", plot_area: "", builtup_area: "", floors: "", scope: "", budget: "", message: "" };

export default function Contact() {
  usePageTitle("Contact — Get a Free Consultation | Sai Leela Decor");
  const c = useContent();
  const CONTACT = c.contact;
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a few words about the project.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry has been received. The studio will respond shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell testid="contact-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20">
        <FadeUp>
          <Eyebrow className="mb-5">Start Your Bungalow Project</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08] text-bone max-w-3xl">
            Get a free <span className="italic text-bronze">consultation</span>
          </h1>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-stone2">
            Tell us about your plot and requirements — we respond with a clear next step.
          </p>
        </FadeUp>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <FadeUp className="lg:col-span-7">
            <form data-testid="enquiry-form" onSubmit={submit} className="space-y-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Full Name *</label>
                  <input data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className="luxury-input mt-2" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Email *</label>
                  <input data-testid="enquiry-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="luxury-input mt-2" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Phone</label>
                  <input data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="luxury-input mt-2" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Plot / Project Location</label>
                  <input data-testid="enquiry-location-input" value={form.location} onChange={set("location")} placeholder="e.g. Karjat, Badlapur, Thane" className="luxury-input mt-2" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Plot Area</label>
                  <input data-testid="enquiry-plot-input" value={form.plot_area} onChange={set("plot_area")} placeholder="e.g. 4,000 sq ft" className="luxury-input mt-2" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Expected Built-up Area</label>
                  <input data-testid="enquiry-builtup-input" value={form.builtup_area} onChange={set("builtup_area")} placeholder="e.g. 3,000 sq ft" className="luxury-input mt-2" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Number of Floors</label>
                  <select data-testid="enquiry-floors-select" value={form.floors} onChange={set("floors")} className="luxury-select mt-2">
                    <option value="">Select</option>
                    {["G (Ground only)", "G+1", "G+2", "G+3 or more"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Scope</label>
                  <select data-testid="enquiry-scope-select" value={form.scope} onChange={set("scope")} className="luxury-select mt-2">
                    <option value="">Select</option>
                    {["Construction Only", "Construction + Interiors", "Interiors Only", "Not sure yet"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Indicative Budget</label>
                <select data-testid="enquiry-budget-select" value={form.budget} onChange={set("budget")} className="luxury-select mt-2">
                  <option value="">Select a range</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Requirements / Message *</label>
                <textarea data-testid="enquiry-message-input" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your plot, your family, and the home you are imagining..." className="luxury-input mt-2 resize-none" />
              </div>
              <button
                type="submit"
                data-testid="enquiry-submit-btn"
                disabled={sending}
                className="inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-10 py-5 transition-all duration-500 hover:bg-bronze-light disabled:opacity-50"
              >
                {sending ? "Sending..." : "Get Free Consultation"} <ArrowRight size={14} />
              </button>
            </form>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-5 space-y-10">
            <div className="border border-white/[0.07] p-8 space-y-7">
              {[
                { icon: Phone, label: "Telephone", value: CONTACT.phone, testid: "contact-phone" },
                { icon: Mail, label: "Email", value: [CONTACT.email, CONTACT.email2].filter(Boolean).join("  ·  "), testid: "contact-email" },
                { icon: MessageCircle, label: "WhatsApp", value: "+91 98336 21791 — chat now", href: c.socials?.whatsapp || "https://wa.me/919833621791", testid: "contact-whatsapp" },
                { icon: MapPin, label: "Studio", value: CONTACT.address, testid: "contact-address" },
                { icon: Clock, label: "Hours", value: CONTACT.hours, testid: "contact-hours" },
              ].map(({ icon: Icon, label, value, testid, href }) => (
                <div key={label} className="flex items-start gap-4">
                  <span className="w-10 h-10 shrink-0 border border-bronze/30 flex items-center justify-center text-bronze">
                    <Icon size={15} />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-muted2 mb-1.5">{label}</p>
                    {href ? (
                      <a data-testid={testid} href={href} target="_blank" rel="noreferrer" className="link-underline text-sm text-bronze font-light leading-relaxed">{value}</a>
                    ) : (
                      <p data-testid={testid} className="text-sm text-bone font-light leading-relaxed">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div data-testid="map-placeholder" className="relative aspect-video border border-white/[0.07] bg-ink overflow-hidden">
              <iframe
                title="Sai Leela Decor — Mohan Palms, Badlapur"
                src="https://www.google.com/maps?q=19.1610877,73.2267362&z=17&output=embed"
                className="w-full h-full border-0 grayscale-[30%] contrast-[1.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </PageShell>
  );
}
